import { Button, Chip, Slider } from "@heroui/react";
import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import React, { useState, useRef } from "react";
import ToolPageLayout from "../../components/tools/ToolPageLayout";
import SignUpPromptModal from "../../components/tools/SignUpPromptModal";

const FlashcardMaker: React.FC = () => {
  const [textInput, setTextInput] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [cardCount, setCardCount] = useState(10);
  const [difficulty, setDifficulty] = useState("mixed");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const difficultyOptions = [
    { id: "easy", label: "Easy", icon: "lucide:smile" },
    { id: "medium", label: "Medium", icon: "lucide:meh" },
    { id: "hard", label: "Hard", icon: "lucide:brain" },
    { id: "mixed", label: "Mixed", icon: "lucide:shuffle" },
  ];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleGenerate = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setShowSignUpModal(true);
    }, 2000);
  };

  const canSubmit = () => {
    return textInput.trim().length > 0 || selectedFile !== null;
  };

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "AI Flashcard Maker - YouWo AI",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Generate study flashcards automatically from any text, PDF, or notes. Free AI-powered flashcard generator for students.",
  };

  const howItWorksSteps = [
    {
      title: "Add Your Content",
      description: "Paste text, upload a PDF, or provide notes you want to study.",
    },
    {
      title: "AI Creates Flashcards",
      description: "Our AI identifies key concepts and generates question-answer pairs.",
    },
    {
      title: "Study & Learn",
      description: "Review your flashcards with spaced repetition for optimal retention.",
    },
  ];

  const benefits = [
    {
      title: "Instant Generation",
      description: "Create hundreds of flashcards from any content in seconds, not hours.",
      icon: "lucide:zap",
    },
    {
      title: "Active Recall",
      description: "Flashcards use active recall - the most effective learning technique proven by science.",
      icon: "lucide:brain",
    },
    {
      title: "100+ Languages",
      description: "Create flashcards from content in over 100 languages.",
      icon: "lucide:globe",
    },
    {
      title: "Difficulty Levels",
      description: "Generate easy, medium, or hard flashcards based on your needs.",
      icon: "lucide:sliders",
    },
    {
      title: "Smart Extraction",
      description: "AI identifies key concepts, definitions, and facts worth remembering.",
      icon: "lucide:lightbulb",
    },
    {
      title: "Export with Pro",
      description: "Export to Anki, Quizlet, or other apps with YouWo AI Pro.",
      icon: "lucide:share-2",
    },
  ];

  const faqs = [
    {
      question: "What is the AI Flashcard Maker?",
      answer: "The AI Flashcard Maker is a free tool that automatically generates study flashcards from any text, PDF, or notes. It identifies key concepts and creates question-answer pairs for effective learning.",
    },
    {
      question: "How many flashcards can I generate?",
      answer: "You can generate up to 25 flashcards for free. Sign up for a free account to generate unlimited flashcards and save them to your study library.",
    },
    {
      question: "Can I edit the generated flashcards?",
      answer: "Yes! All generated flashcards can be edited, reworded, or deleted. You can also add your own flashcards to the generated set.",
    },
    {
      question: "Does it work with any subject?",
      answer: "Absolutely! Our AI works with any subject - from biology and history to programming and languages. It adapts to the content you provide.",
    },
    {
      question: "Can I export flashcards to Anki or Quizlet?",
      answer: "Yes! With YouWo AI Pro, you can export your flashcards in formats compatible with Anki, Quizlet, and other popular flashcard apps.",
    },
    {
      question: "How do flashcards help me learn?",
      answer: "Flashcards use active recall - you're actively retrieving information from memory rather than passively reading. This is scientifically proven to be the most effective learning technique.",
    },
  ];

  const relatedTools = [
    {
      name: "AI Quiz Generator",
      description: "Create practice quizzes to test your understanding of any topic.",
      href: "/tools/quiz-generator",
      icon: "lucide:help-circle",
      iconColor: "text-green-500",
    },
    {
      name: "PDF Summarizer",
      description: "Turn lengthy PDFs and documents into clear, concise summaries instantly.",
      href: "/tools/pdf-summarizer",
      icon: "lucide:file-text",
      iconColor: "text-orange-500",
    },
    {
      name: "Lecture Notes AI",
      description: "Transform lecture recordings into comprehensive study notes.",
      href: "/tools/lecture-notes",
      icon: "lucide:graduation-cap",
      iconColor: "text-emerald-500",
    },
    {
      name: "YouTube Summarizer",
      description: "Transform YouTube videos into concise summaries with timestamps.",
      href: "/tools/youtube-summarizer",
      icon: "lucide:youtube",
      iconColor: "text-red-500",
    },
  ];

  return (
    <>
      <ToolPageLayout
        title="Free AI Flashcard Maker - Generate Study Cards Instantly | YouWo AI"
        metaDescription="Generate study flashcards automatically from any text, PDF, or notes using AI. Free flashcard generator with active recall learning. Export to Anki/Quizlet with Pro."
        keywords="flashcard maker, ai flashcards, study flashcards, anki generator, quizlet alternative, flashcard generator, study tool"
        canonicalUrl="https://youwo.ai/tools/flashcard-maker"
        structuredData={structuredData}
        heroTitle="AI Flashcard Maker"
        heroSubtitle="Generate flashcards from any text, PDF, or notes using AI. Active recall is the most effective learning technique - let AI create the cards so you can focus on studying."
        heroIcon="lucide:layers"
        heroIconColor="text-purple-500"
        heroIconBg="bg-purple-50"
        howItWorksSteps={howItWorksSteps}
        benefitsTitle="Why Choose YouWo AI Flashcard Maker?"
        benefits={benefits}
        faqs={faqs}
        relatedTools={relatedTools}
      >
        {/* Tool Input Area */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8">
          {/* Text Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Paste your study material
            </label>
            <textarea
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Paste lecture notes, textbook content, or any text you want to study..."
              className="w-full h-40 p-4 border border-gray-200 rounded-xl resize-none focus:outline-none focus:border-cornflowerblue"
            />
          </div>

          {/* Or Divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-sm text-gray-400">or upload a file</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* File Upload */}
          <div
            className={`border-2 border-dashed rounded-xl p-4 text-center transition-colors cursor-pointer mb-6 ${
              isDragging
                ? "border-cornflowerblue bg-blue-50"
                : selectedFile
                ? "border-green-400 bg-green-50"
                : "border-gray-200 hover:border-cornflowerblue"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept=".pdf,.txt,.doc,.docx"
              onChange={handleFileSelect}
            />

            {selectedFile ? (
              <div className="flex items-center justify-center gap-3">
                <Icon icon="lucide:file-check" className="text-green-600 text-xl" />
                <span className="text-gray-900">{selectedFile.name}</span>
                <Button
                  variant="light"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                >
                  Remove
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <Icon icon="lucide:upload" className="text-xl" />
                <span>Upload PDF, DOC, or TXT file</span>
              </div>
            )}
          </div>

          {/* Settings */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Card Count */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Number of flashcards: {cardCount}
              </label>
              <Slider
                size="sm"
                step={5}
                minValue={5}
                maxValue={50}
                value={cardCount}
                onChange={(val) => setCardCount(val as number)}
                className="max-w-full"
                classNames={{
                  track: "bg-gray-200",
                  filler: "bg-cornflowerblue",
                }}
              />
            </div>

            {/* Difficulty */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Difficulty Level
              </label>
              <div className="flex flex-wrap gap-2">
                {difficultyOptions.map((option) => (
                  <Chip
                    key={option.id}
                    variant={difficulty === option.id ? "solid" : "bordered"}
                    color={difficulty === option.id ? "primary" : "default"}
                    className={`cursor-pointer transition-all ${
                      difficulty === option.id
                        ? "bg-cornflowerblue text-white"
                        : "border-gray-200 text-gray-600 hover:border-cornflowerblue"
                    }`}
                    startContent={<Icon icon={option.icon} className="text-sm" />}
                    onClick={() => setDifficulty(option.id)}
                  >
                    {option.label}
                  </Chip>
                ))}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            color="primary"
            size="lg"
            className="w-full bg-cornflowerblue h-14 font-semibold"
            isLoading={isLoading}
            isDisabled={!canSubmit()}
            onClick={handleGenerate}
          >
            {isLoading ? "Generating Flashcards..." : "Generate Flashcards"}
          </Button>
        </div>
      </ToolPageLayout>

      <SignUpPromptModal
        isOpen={showSignUpModal}
        onClose={() => setShowSignUpModal(false)}
        toolName="Flashcards"
      />
    </>
  );
};

export default FlashcardMaker;
